import { uuidv7 } from "uuidv7"

import { uuidToNumber } from "~/utils/entityId"
import { addSearchToBuf } from "~/utils/flatbuffers/search"

import { asTimestampMillis, keyFor, PropertyId, PropertySchema, PropertyStatus } from "../entity"
import { BaseEntity } from "../entity/BaseEntity"

export abstract class BaseSearch extends BaseEntity<SearchProperty, Search> {
  static clientPreHashId() {
    return `00050000-${uuidv7()}`
  }

  addToBuf(builder: DatagramBufferBuilder) {
    return addSearchToBuf(builder, this.inner())
  }

  executedAt(): number | undefined {
    const property = this.inner().properties[PropertyId.SEARCH_EXECUTED_AT]
    if (!property)
      return
    if (property.value.type !== PropertySchema.UtcTimestampMillisValue)
      throw new Error("Invalid search intent schema")
    return property.value.utcTimestampMillis
  }

  searchResults(): Record<number, SearchResult> {
    const results = Object.values(this.inner().properties)
      .filter(property => property!.value.type === PropertySchema.SearchResultValue)
      .map((property) => {
        if (!isDefined(property))
          throw new Error("No property")
        if (property!.value.type !== PropertySchema.SearchResultValue)
          throw new Error("Invalid search result schema")
        const uuid = keyFor(property.propertyId as PropertyId)
        const index = uuidToNumber(uuid)
        return [index, property!.value.result]
      })
    return Object.fromEntries(results)
  }

  description() {
    const property = this.inner().properties[PropertyId.SEARCH_DESCRIPTION]
    if (!property)
      return
    if (property.value.type !== PropertySchema.NonEmptyStringValue)
      throw new Error("Invalid search description schema")
    return property.value.nonEmptyString
  }

  definition() {
    const property = this.inner().properties[PropertyId.SEARCH_DEFINITION]
    if (!property)
      return
    if (property.value.type !== PropertySchema.SearchDefinitionValue)
      throw new Error("Invalid search definition schema")
    return property.value.definition
  }

  addSearchLocations(locations: string[], timestamp: string) {
    this.addProperty({
      propertyId: PropertyId.SEARCH_LOCATIONS,
      id: this.id,
      schema: PropertySchema.SearchLocationsValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.SearchLocationsValue, locations },
    })
  }

  addSearchDefinition(definition: SearchDefinition, timestamp: string) {
    this.addProperty({
      propertyId: PropertyId.SEARCH_DEFINITION,
      id: this.id,
      schema: PropertySchema.SearchDefinitionValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.SearchDefinitionValue, definition },
    })
  }

  addExecutedAt(timestamp: string) {
    const utcTimestampMillis = asTimestampMillis(timestamp)
    this.addProperty({
      propertyId: PropertyId.SEARCH_EXECUTED_AT,
      id: this.id,
      schema: PropertySchema.UtcTimestampMillisValue,
      timestamp,
      version: 0,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis },
    })
  }
}
